import { useState, useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { $currentUser, setAuth, $accessToken } from '~/lib/stores/auth';
import { apiUpdateProfile } from '~/lib/api/user-settings';

export function ProfileTab() {
  const user = useStore($currentUser);
  const accessToken = useStore($accessToken);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName || '');
  }, [user?.displayName]);

  const dirty = (user?.displayName || '') !== displayName.trim();

  async function handleSave() {
    if (!dirty || !displayName.trim() || !user) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaved(false);

    try {
      const updated = await apiUpdateProfile({ displayName: displayName.trim() });
      setAuth(accessToken!, { ...user, displayName: updated.displayName ?? displayName.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to update name:', err);
      setSaveError('Could not save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setDisplayName(user?.displayName || '');
    setSaveError(null);
  }

  const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();

  return (
    <div>
      {/* Page header */}
      <h3 className="ui-serif m-0 mb-4.5 text-[24px]">Profile</h3>

      {/* Identity card */}
      <div className="rounded-xl border border-hairline bg-raised px-5.5 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-15 w-15 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-[24px] font-semibold text-[#0A0A0A]">
            {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" /> : initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="ui-serif overflow-hidden text-ellipsis whitespace-nowrap text-[19px] leading-tight text-primary">
              {user?.displayName || 'Unnamed'}
            </div>
            <div className="mt-1 overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] text-muted">
              {user?.email}
            </div>
          </div>
        </div>
      </div>

      {/* Details section label */}
      <div className="ui-caption mb-2.5 mt-6.5">Details</div>

      {/* Details card */}
      <div className="rounded-xl border border-hairline bg-raised px-5.5 py-5">
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <div>
            <label className="ui-label">Display name</label>
            <input
              className="ui-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              maxLength={100}
            />
          </div>
          <div>
            <label className="ui-label flex items-center gap-1.5">
              Email
              <span className="i-ph:lock-simple text-[11px] text-faint opacity-70" title="Email cannot be changed" />
            </label>
            <input className="ui-input cursor-not-allowed opacity-70" value={user?.email || ''} readOnly />
          </div>
        </div>

        {saveError && (
          <div className="mt-3.5 flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/[0.07] px-3 py-2.5 text-[12.5px] leading-normal text-danger">
            <span className="i-ph:warning-circle text-[14px]" />
            {saveError}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="mt-5 flex items-center justify-end gap-2">
        {saved && (
          <span className="mr-auto flex items-center gap-1.5 text-[12.5px] text-success">
            <span className="i-ph:check-circle-fill text-[14px]" />
            Saved
          </span>
        )}
        <button className="ui-btn-ghost" onClick={handleCancel} disabled={!dirty || isSaving}>
          Cancel
        </button>
        <button className="ui-btn-accent" onClick={handleSave} disabled={!dirty || !displayName.trim() || isSaving}>
          {isSaving ? (
            <>
              <span className="i-ph:spinner-gap animate-spin" />
              Saving…
            </>
          ) : (
            'Save changes'
          )}
        </button>
      </div>
    </div>
  );
}
